// The item panel's "On order": open PO lines for this item (what's still to arrive and when), and
// a quick way to order more from its preferred vendor (added to the open draft for that vendor, ship-to and kind). The
// usual table (it scrolls inside past 12), its View and Columns in the row under the title.
import { useMemo, useState } from "react";
import type { Po, PoLine } from "../../../../shared/db.gen.ts";
import { isOpen, PO_STATUS_LABELS } from "../../../../shared/po.ts";
import { dayText, qty } from "../../lib/format.ts";
import { can } from "../../lib/mutate.ts";
import { usePlan } from "../../lib/planning.ts";
import { navigate } from "../../lib/router.tsx";
import { useRows } from "../../lib/sync.ts";
import { Badge } from "../../ui/controls.tsx";
import { type Column, DataTable } from "../../ui/DataTable.tsx";
import { Section } from "../../ui/RecordPanel.tsx";
import { PaneLink, usePaneOpen } from "../../ui/SidePane.tsx";
import { toast } from "../../ui/toast.tsx";
import { addLines, createPo, openDraftFor, poPath, STATUS_TONE, useLinesByPo, vendorLabel } from "./lib.ts";

type Row = { id: string; line: PoLine; po: Po; left: number; vendor: string; date: string | null };

export function OnOrder({ itemId }: { itemId: string }) {
  const paneOpen = usePaneOpen(); // Side pane --- the draft it was added to opens beside it
  const lines = useRows("po_line");
  const pos = useRows("po");
  const vendors = useRows("vendor");
  const links = useRows("vendor_item");
  const items = useRows("item");
  const linesByPo = useLinesByPo();
  const plan = usePlan();
  const [slot, setSlot] = useState<HTMLDivElement | null>(null); // Table views --- the table's View and Columns

  const open = useMemo<Row[]>(() => {
    const poById = new Map(pos.map((p) => [p.id, p]));
    return lines
      .filter((l) => l.item_id === itemId)
      .flatMap((l) => {
        const po = poById.get(l.po_id);
        return po && isOpen(po.status)
          ? [
              {
                id: l.id,
                line: l,
                po,
                left: Math.max(0, Number(l.qty) - Number(l.qty_received)),
                vendor: vendorLabel(vendors.find((v) => v.id === po.vendor_id)),
                date: l.promised_date ?? po.expected_arrive ?? po.expected_ship,
              },
            ]
          : [];
      });
  }, [lines, pos, vendors, itemId]);

  // Still to arrive: what's left on sent POs (a draft isn't coming yet)
  const stillToArrive = (rows: Row[]) => rows.filter((o) => o.po.status !== "draft").reduce((n, o) => n + o.left, 0);
  const columns: Column<Row>[] = [
    {
      key: "po",
      header: "PO",
      width: "84px",
      value: (r) => r.po.number,
      cell: (r) => (
        <PaneLink kind="po" id={r.po.id} to={poPath(r.po.id)} className="font-mono text-[12px] font-medium hover:underline">
          {r.po.number}
        </PaneLink>
      ),
    },
    { key: "vendor", header: "Vendor", width: "minmax(110px,1fr)", cell: (r) => <span className="truncate">{r.vendor}</span> },
    {
      key: "status",
      header: "Status",
      width: "120px",
      value: (r) => PO_STATUS_LABELS[r.po.status],
      cell: (r) => <Badge tone={STATUS_TONE[r.po.status]}>{PO_STATUS_LABELS[r.po.status]}</Badge>,
    },
    {
      key: "date",
      header: "Arrives",
      headerTitle: "The vendor's promised date, else when the PO is needed by, else when it ships",
      width: "84px",
      // Without a date: last
      value: (r) => r.line.promised_date ?? r.po.expected_arrive ?? "9",
      text: (r) => dayText(r.date),
      cell: (r) => <span className="text-[12px] text-muted">{dayText(r.date)}</span>,
    },
    {
      key: "left",
      header: "Left",
      width: "72px",
      align: "right",
      cell: (r) => <span className="font-mono text-[12px]">{qty(r.left)}</span>,
      total: (rows) => (
        <span className="font-mono" title="Still to arrive on sent POs (drafts aren't counted)">
          {qty(stillToArrive(rows))}
        </span>
      ),
    },
  ];

  // Adds the item to the open purchase draft for its preferred vendor and where the plan ships it (a part a CM builds
  // with goes to that CM), never a make draft or one shipping elsewhere (openDraftFor()); else starts one. Builds are
  // ordered from the Make plan.
  const order = async () => {
    const r = plan.byItem.get(itemId);
    if (r?.role === "make") {
      toast.info("Builds are ordered from the Make plan");
      return navigate(`/make?item=${itemId}`);
    }
    const vendorLinks = links.filter((l) => l.item_id === itemId);
    const link = vendorLinks.find((l) => l.is_preferred) ?? vendorLinks[0];
    const vendor = link && vendors.find((v) => v.id === link.vendor_id);
    const item = items.find((i) => i.id === itemId);
    if (!link || !vendor || !item) return toast.error("Add a vendor to this item first");
    // Where the plan ships it; with no place from the plan, where the vendor's POs go (as a new one would).
    const shipTo = (r?.vendor?.id === vendor.id && r.shipTo) || undefined;
    const draft = openDraftFor(vendor, shipTo, "purchase");
    if (!draft) return createPo(vendor, [{ item, link }], "purchase", shipTo);
    if (await addLines(draft, linesByPo.get(draft.id) ?? [], [{ item, link }])) paneOpen("po", draft.id);
  };

  return (
    <Section title="On order" count={open.length} slot={setSlot} addLabel="Order" onAdd={can("po.add_lines") ? order : undefined}>
      {!open.length ? (
        <div className="px-2 pb-2 text-muted">Not on any open PO.</div>
      ) : (
        <>
          <div className="px-2 pb-2">
            <DataTable
              fit
              rows={open}
              columns={columns}
              defaultSort={{ key: "date" }}
              onOpen={(r) => paneOpen("po", r.po.id)}
              exportName="Item on order"
              viewsSlot={slot}
              pageFilters={false}
              rowNoun={["line", "lines"]}
            />
          </div>
        </>
      )}
    </Section>
  );
}
